import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import ClippySVG from './ClippySVG';
import { heraldKey, parseMark, discoverNew, bubbleCopy } from '../utils/heraldLogic';

// Slice E8 — the mascot notification herald (POLLING version; E8b is the
// named realtime successor). One bubble per discovery, never a parade; the
// bell remains the persistent record. This component is deliberately
// READ-ONLY against notifications: it never touches is_read — a bubble
// shown is not a notification read. Its poll also rebroadcasts the fresh
// payload so the bell badge stays current (the gate's one sanctioned bell
// touch is a listener for this event).
export const HERALD_REFRESH_EVENT = 'gvc:notifications-refreshed';
const POLL_MS = 45000; // mid-window of the owner's 30-60s ruling
const BUBBLE_MS = 10000;

const NotificationHerald = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bubble, setBubble] = useState(null); // { count, newest }
  const [leaving, setLeaving] = useState(false);
  const dismissTimer = useRef(null);
  const reducedMotion = useRef(
    typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  const dismiss = useCallback(() => {
    clearTimeout(dismissTimer.current);
    if (reducedMotion.current) {
      setBubble(null);
      return;
    }
    setLeaving(true);
    setTimeout(() => { setLeaving(false); setBubble(null); }, 250);
  }, []);

  const showBubble = useCallback((count, newest) => {
    // In-place replacement on a burst during a visible bubble — no queueing.
    setLeaving(false);
    setBubble({ count, newest });
    clearTimeout(dismissTimer.current);
    dismissTimer.current = setTimeout(dismiss, BUBBLE_MS);
  }, [dismiss]);

  const poll = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await api.get('/notifications?limit=20');
      const rows = response.data.notifications || [];
      // Freshness rebroadcast for the bell (badge only — read state untouched).
      window.dispatchEvent(new CustomEvent(HERALD_REFRESH_EVENT, {
        detail: { notifications: rows, unreadCount: response.data.unreadCount || 0 }
      }));

      const key = heraldKey(user.id);
      // Re-read FRESH right before deciding — a sibling tab that already
      // heralded these rows advanced the mark, and we stay quiet.
      const mark = parseMark(localStorage.getItem(key));
      if (mark === null) {
        // First run on this browser/user: initialize to NOW, silently.
        // The backlog is never heralded.
        localStorage.setItem(key, new Date().toISOString());
        return;
      }
      const { count, newest, nextHwm } = discoverNew(rows, mark);
      if (count === 0) return;
      // The mark advances ON SHOW — an ignored bubble must not re-nag, and
      // the bell's is_read state is not ours to consume.
      localStorage.setItem(key, nextHwm);
      showBubble(count, newest);
    } catch (err) {
      // Polling is best-effort; the bell remains the persistent record.
    }
  }, [user, showBubble]);

  useEffect(() => {
    if (!user?.id) return undefined;
    poll();
    let interval = setInterval(() => {
      if (!document.hidden) poll();
    }, POLL_MS);
    // Visibility-aware: hidden tabs skip fetches entirely; returning to the
    // tab checks immediately rather than waiting out the interval.
    const onVisible = () => {
      if (!document.hidden) poll();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      clearTimeout(dismissTimer.current);
    };
  }, [user, poll]);

  if (!bubble) return null;

  const { count, newest } = bubble;
  const animation = reducedMotion.current
    ? 'none'
    : (leaving ? 'heraldOut 0.25s ease-in forwards' : 'heraldIn 0.3s ease-out');

  return (
    <div style={{ ...styles.container, animation }} data-herald>
      <div style={styles.bubble}>
        <button
          style={styles.closeButton}
          aria-label="Dismiss notification"
          onClick={(e) => { e.stopPropagation(); dismiss(); }}
        >
          ×
        </button>
        <div
          style={styles.bubbleText}
          role="button"
          tabIndex={0}
          onClick={() => { dismiss(); if (newest.link) navigate(newest.link); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { dismiss(); if (newest.link) navigate(newest.link); } }}
        >
          🔔 {bubbleCopy(count, newest)}
        </div>
        <div style={styles.bubbleArrow} />
      </div>
      <ClippySVG size={72} animated={!reducedMotion.current} />
      <style>{`
        @keyframes heraldIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes heraldOut {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(16px); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  // The BusMascot corner (login-page muscle memory), but z 999 — ONE BELOW
  // .modal-overlay's 1000 so the herald can never sit on top of a modal.
  // Toasts render top-right (App.js ToastContainer) — opposite corner.
  container: {
    position: 'fixed',
    bottom: '80px',
    right: '24px',
    zIndex: 999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end'
  },
  bubble: {
    position: 'relative',
    background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
    padding: '14px 18px',
    borderRadius: '16px',
    marginBottom: '12px',
    maxWidth: '300px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3), 0 0 20px rgba(245, 158, 11, 0.15)',
    border: '2px solid #F59E0B'
  },
  bubbleText: {
    fontSize: '14px',
    color: '#F59E0B',
    lineHeight: '1.4',
    fontWeight: '500',
    cursor: 'pointer'
  },
  bubbleArrow: {
    position: 'absolute',
    bottom: '-10px',
    right: '36px',
    width: 0,
    height: 0,
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderTop: '10px solid #1a1a1a'
  },
  closeButton: {
    position: 'absolute',
    top: '-8px',
    right: '-8px',
    width: '22px',
    height: '22px',
    background: '#2d2d2d',
    color: '#A0A0A0',
    border: '1px solid #444',
    borderRadius: '50%',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
};

export default NotificationHerald;
